/**
 * ONE restaurant-account sign-in for every PWA (KDS, Expo, Display, POS,
 * Kiosk set-up, Time Clock, Admin).
 *
 * The account is the merchant (owner / manager) login: POST /api/merchant/login
 * (+ the SMS / authenticator code step with its mfa_token) or
 * POST /api/merchant/google-login. Its token names the restaurant; every PWA
 * call sends it as `Authorization: Bearer …` (and `X-Merchant-ID` when the
 * owner picked another of their locations for this device), so the backend
 * answers only that restaurant's data.
 *
 * Stored once per origin under one key; a 401 from any account call signs the
 * device out everywhere (the sign-in screen comes back with the reason).
 */

import axios from 'axios';
import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const KEY = 'chain29_pwa_account';
const CHANGED = 'chain29-pwa-account-changed';

let signedOutReason = null;

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    const account = raw ? JSON.parse(raw) : null;
    return account && account.token && account.restaurant?.id ? account : null;
  } catch (e) {
    return null;
  }
}

function announce() {
  window.dispatchEvent(new Event(CHANGED));
}

/** The signed-in account: { token, restaurant: {id, name}, manager, restaurants } or null. */
export function getAccount() {
  return read();
}

export function getAccountToken() {
  return read()?.token || null;
}

export function getAccountRestaurant() {
  return read()?.restaurant || null;
}

/** Headers for a fetch() made on the account's behalf. */
export function accountHeaders(extra = {}) {
  const account = read();
  if (!account) return { ...extra };
  const headers = { ...extra, Authorization: `Bearer ${account.token}` };
  if (String(account.restaurant.id) !== String(account.tokenRestaurantId)) {
    headers['X-Merchant-ID'] = String(account.restaurant.id);
  }
  return headers;
}

/**
 * Save a sign-in answer (login / code step / Google). `restaurantId` is the
 * location this device is for (default: the account's primary one).
 */
export function saveAccount(loginResponse, restaurantId) {
  const data = loginResponse || {};
  const restaurants = Array.isArray(data.restaurants) ? data.restaurants : [];
  const primaryId = data.primary_restaurant_id ?? data.manager?.restaurant_id ?? restaurants[0]?.id;
  const chosenId = restaurantId ?? primaryId;
  if (!data.access_token || !chosenId) {
    throw new Error('This account is not linked to a restaurant yet');
  }
  const chosen = restaurants.find(r => String(r.id) === String(chosenId));
  const account = {
    token: data.access_token,
    tokenRestaurantId: primaryId,
    restaurant: { id: Number(chosenId), name: chosen?.name || null },
    restaurants: restaurants.map(r => ({ id: r.id, name: r.name })),
    manager: data.manager ? { id: data.manager.id, email: data.manager.email, full_name: data.manager.full_name } : null,
    signedInAt: new Date().toISOString(),
  };
  localStorage.setItem(KEY, JSON.stringify(account));
  signedOutReason = null;
  announce();
  return account;
}

/** Sign this device out of the account (every PWA on this origin). */
export function signOutAccount(reason = null) {
  localStorage.removeItem(KEY);
  signedOutReason = reason;
  announce();
}

/** Why the device was signed out (e.g. session expired), shown once on the sign-in screen. */
export function takeSignedOutReason() {
  const reason = signedOutReason;
  signedOutReason = null;
  return reason;
}

/** React hook: the current account, re-rendered on sign-in / sign-out (also from other tabs). */
export function useAccount() {
  const [account, setAccount] = useState(read);
  useEffect(() => {
    const update = () => setAccount(read());
    const onStorage = (e) => { if (e.key === KEY) update(); };
    window.addEventListener(CHANGED, update);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGED, update);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return account;
}

/**
 * The axios instance every PWA uses for its account calls: base URL, the
 * account token on every request, and a 401 signs the device out.
 */
export const accountApi = axios.create({ baseURL: API });

accountApi.interceptors.request.use((config) => {
  config.headers = { ...(config.headers || {}), ...accountHeaders() };
  return config;
});

accountApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && read()) {
      signOutAccount('Your session ended. Please sign in again.');
    }
    return Promise.reject(error);
  }
);

/** A readable message for an API error (never an object). */
export function apiErrorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (!error?.response) return 'Cannot reach the server. Check the connection and try again.';
  return fallback;
}
